import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Upload, Users, Search, CreditCard, LogOut, Network, Sparkles, Shield, FolderKanban, Building2 } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/search", label: "Ask Network", icon: Search, testId: "nav-search" },
  { to: "/lists", label: "Lead Lists", icon: FolderKanban, testId: "nav-lists" },
  { to: "/companies", label: "Companies", icon: Building2, testId: "nav-companies" },
  { to: "/contacts", label: "Contacts", icon: Users, testId: "nav-contacts" },
  { to: "/upload", label: "Import Data", icon: Upload, testId: "nav-upload" },
  { to: "/billing", label: "Subscription", icon: CreditCard, testId: "nav-billing" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPaid = user?.is_paid;
  const nav = user?.role === "admin"
    ? [...NAV, { to: "/admin", label: "Admin", icon: Shield, testId: "nav-admin" }]
    : NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex">
      <aside className="hidden md:flex w-64 flex-col border-r border-[#1E2A3F] bg-[#0D121C] fixed h-screen">
        <div className="px-5 py-6 flex items-center border-b border-[#1E2A3F]">
          <Link to="/" data-testid="sidebar-logo-link" className="inline-flex transition-opacity hover:opacity-80">
            <img src="/linkviewer-logo.png" alt="LinkViewer.PRO — home" className="h-9 w-auto" data-testid="sidebar-logo" />
          </Link>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
                  isActive ? "bg-blue-600/15 text-blue-300 border border-blue-500/20" : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-5 space-y-2">
          <Link to="/account" data-testid="account-link" className="block px-3 py-3 rounded-lg bg-[#111726] border border-[#27334D] hover:border-blue-500/50 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-xs font-semibold text-blue-300">
                {(user?.name || user?.email || "?")[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{user?.name || "You"}</div>
                <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
              </div>
            </div>
            <div className="mt-2.5" data-testid="user-subscription-badge">
              {isPaid ? (
                <span className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-mono-niq uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/25">
                  <Sparkles className="w-3 h-3" /> Pro Plan
                </span>
              ) : (
                <span className="w-full inline-flex items-center justify-center text-[10px] font-mono-niq uppercase tracking-wider px-2 py-1 rounded-md bg-slate-500/10 text-slate-400 border border-slate-500/25">
                  Free
                </span>
              )}
            </div>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 min-h-screen">
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[#1E2A3F] bg-[#0D121C] sticky top-0 z-30">
          <Link to="/" className="flex items-center transition-opacity hover:opacity-80">
            <img src="/linkviewer-logo.png" alt="LinkViewer.PRO — home" className="h-6 w-auto" />
          </Link>
          <button onClick={handleLogout} className="text-slate-400"><LogOut className="w-4 h-4" /></button>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">{children}</div>
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-[#0D121C] border-t border-[#1E2A3F] flex justify-around py-2 z-30">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${isActive ? "text-blue-400" : "text-slate-500"}`}>
              <item.icon className="w-5 h-5" />
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
}
